import type { FilterModel } from 'admin/src/types/Filter';
import type { ColDef, RowSelectedEvent, SelectionEventSourceType } from 'ag-grid-enterprise';
import type { GridApi } from 'ag-grid-enterprise';
import type { AgGridReact } from 'ag-grid-react';
import { debounce } from 'lodash';
import { debounce as nuqsDebounce, useQueryState } from 'nuqs';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { MutableRefObject, ReactNode, RefObject } from 'react';
import { cn } from 'shared/src/utils';

type DataGridContextValueProp = {
  filters: FilterModel[];
  colDefs: ColDef[];
  gridApi?: GridApi;
  setGridApi: (api?: GridApi) => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  agGridRef: RefObject<AgGridReact>;
  localSearch: string;
  setLocalSearch: (value: string) => void;
  clearUrlSearchRef: MutableRefObject<(() => void) | null>;
  clearFilters: () => void;
};

const searchQueryOptions = { history: 'push' as const, limitUrlUpdates: nuqsDebounce(200) };

const DataGridContext = createContext<DataGridContextValueProp>({
  agGridRef: { current: null },
  gridApi: undefined,
  setGridApi: (_api?: GridApi) => {},
  loading: false,
  setLoading: (_loading: boolean) => {},
  filters: [],
  colDefs: [],
  localSearch: '',
  setLocalSearch: (_value: string) => {},
  clearUrlSearchRef: { current: null },
  clearFilters: () => {},
});

export function useGridContext() {
  const context = useContext(DataGridContext);

  if (!context) {
    throw new Error('useGridContext can only be used as a child of <DataGrid>');
  }

  const { clearUrlSearchRef: _clearUrlSearchRef, ...publicContext } = context;
  return publicContext;
}

export function useDataGridSearch({ persistState = true }: { persistState?: boolean } = {}) {
  const context = useContext(DataGridContext);

  if (!context) {
    throw new Error('useDataGridSearch can only be used as a child of <DataGrid>');
  }

  const { localSearch, setLocalSearch, clearUrlSearchRef } = context;
  const [urlSearch, setUrlSearch] = useQueryState('search', searchQueryOptions);

  const search = persistState ? urlSearch : localSearch;

  const setSearch = useCallback(
    (value: string | null) => {
      if (persistState) {
        void setUrlSearch(value);
      } else {
        setLocalSearch(value || '');
      }
    },
    [persistState, setLocalSearch, setUrlSearch],
  );

  useEffect(() => {
    if (!persistState) return;

    clearUrlSearchRef.current = () => {
      void setUrlSearch(null);
    };

    return () => {
      clearUrlSearchRef.current = null;
    };
  }, [clearUrlSearchRef, persistState, setUrlSearch]);

  return { search: search || '', setSearch };
}

export function useGridSelectedRow<T>(): { selectedRow?: T; selectedRows?: T[]; lastSelectionSource?: SelectionEventSourceType; deselectRow: () => void } {
  const { gridApi, setGridApi } = useGridContext();
  const [selectedRow, setSelectedRow] = useState<T>();
  const [selectedRows, setSelectedRows] = useState<T[]>();
  const [lastSelectionSource, setLastSelectionSource] = useState<SelectionEventSourceType>();

  useEffect(() => {
    if (!selectedRows?.length) {
      setSelectedRow(undefined);
      return;
    }

    setSelectedRow(selectedRows[selectedRows?.length - 1]);
  }, [selectedRows]);

  useEffect(() => {
    if (!gridApi) return;

    setSelectedRows(gridApi.getSelectedRows());
  }, [gridApi]);

  useEffect(() => {
    void setGridApi;
    if (!gridApi) return;

    const onRowSelected = debounce((params: RowSelectedEvent<T>) => {
      const selectedRows = gridApi?.getSelectedRows() || [];

      setLastSelectionSource(params.source);
      setSelectedRows(selectedRows);
    }, 100);

    function onRowDataUpdated() {
      const selectedRows = gridApi?.getSelectedRows();

      setSelectedRows(selectedRows);
    }

    gridApi.addEventListener('rowDataUpdated', onRowDataUpdated);
    gridApi.addEventListener('rowSelected', onRowSelected);

    return () => {
      if (gridApi.isDestroyed()) return;

      gridApi.removeEventListener('rowDataUpdated', onRowDataUpdated);
      gridApi.removeEventListener('rowSelected', onRowSelected);
    };
  }, [gridApi, setGridApi]);

  function deselectRow() {
    gridApi?.deselectAll();
  }

  return { selectedRow, lastSelectionSource, selectedRows, deselectRow };
}

export function DataGridProvider({ children, className }: { children: ReactNode; className?: string }) {
  const agGridRef = useRef(null);
  const clearUrlSearchRef = useRef<(() => void) | null>(null);
  const [gridApi, setGridApi] = useState<GridApi>();
  const [loading, setLoading] = useState(false);
  const [localSearch, setLocalSearch] = useState('');

  const clearFilters = useCallback(() => {
    if (gridApi && !gridApi.isDestroyed()) {
      gridApi.setGridOption('quickFilterText', '');
      gridApi.setFilterModel(null);
    }
    clearUrlSearchRef.current?.();
    setLocalSearch('');
  }, [gridApi]);

  return (
    <DataGridContext.Provider
      value={{
        agGridRef,
        gridApi,
        setGridApi,
        loading,
        setLoading,
        filters: [],
        colDefs: [],
        localSearch,
        setLocalSearch,
        clearUrlSearchRef,
        clearFilters,
      }}
    >
      <div className={cn('flex flex-col h-full w-full space-y-3', className)}>{children}</div>
    </DataGridContext.Provider>
  );
}
